/*
  Hardcoded about section content for google gateways.
  This will only be displayed to users in India when this experiment is active
  https://tools.coursera.org/epic/experiment/qUohUPunEeuptMG5qj0t0g
*/
import type { CmlContent } from '__generated__/graphql-types';

import { GOOGLE_GATEWAYS_FOR_OVERRIDES_SLUGS } from 'bundles/unified-description-page-common/constants';

import _t from 'i18n!nls/xdp';

type AboutCmlDescriptionOverrides = {
  [slug: string]: (arg0: number) => CmlContent;
};

type AboutDescriptionOverrides = {
  [slug: string]: (arg0: number) => string;
};

// Keys are XDP slugs.
export const aboutCmlDescriptionOverrides: AboutCmlDescriptionOverrides = {
  [GOOGLE_GATEWAYS_FOR_OVERRIDES_SLUGS.GOOGLE_IT_SUPPORT]: (productPrice: number) => {
    return {
      dtdId: '1',
      htmlWithMetadata: {
        html: '',
        metadata: {
          hasAssetBlock: false,
          hasCodeBlock: false,
          hasMath: false,
          isPlainText: true,
        },
      },
      cmlValue: `
      <co-content>
        <text>${_t(
          'Prepare for a career in the high-growth field of IT, no experience required. Get professional training designed by Google and get on the fast-track to a competitively paid job.'
        )}</text>
        <text>${_t(
          'Over 5 courses, learn in-demand skills that prepare you for an entry-level job. At under 10 hours per week, you can complete the certificate in less than 6 months.'
        )}</text>
        <text><strong>${_t(
          'Since the IT Support Certificate launched in 2018, 82% of graduates have reported a positive career outcome like a new job, promotion, or raise within 6 months.¹'
        )}</strong></text>
        <text><strong>${_t('If you’re interested in building on your IT foundations, check out the #{product}', {
          product: '',
        })}<a href="https://www.coursera.org/professional-certificates/google-it-automation">Google IT Automation with Python Professional Certificate</a>.</strong></text>
        <text>${_t('Check out all Google Career Certificates #{here}', {
          here: '',
        })}<a href="https://www.coursera.org/in/google-career-certificates">here</a>. <strong>${_t(
        'Now INR #{productPrice}/month',
        { productPrice }
      )} </strong>\n${_t('The certificate can be completed in less than 6 months of part time study.')}</text>
        <text>${_t(
          '¹Coursera Learner Outcome Survey, all time for Google IT Support Certificate. Data is collected via on-platform surveys or email and is cumulative from 1/18 - 1/21.'
        )}</text>
      </co-content>
    `,
    };
  },
  [GOOGLE_GATEWAYS_FOR_OVERRIDES_SLUGS.GOOGLE_DATA_ANALYTICS]: (productPrice: number) => {
    return {
      dtdId: '1',
      htmlWithMetadata: {
        html: '',
        metadata: {
          hasAssetBlock: false,
          hasCodeBlock: false,
          hasMath: false,
          isPlainText: true,
        },
      },
      cmlValue: `
      <co-content>
        <text>${_t(
          'Get professional training designed by Google and prepare for a new career in the high-growth field of data analytics, no experience required.'
        )}</text>
        <text><strong>Data analytics</strong>${_t(
          '#{subject} is the collection, transformation, and organization of data in order to draw conclusions, make predictions, and drive informed decision making.',
          { subject: '' }
        )}</text>
        <text>${_t(
          'Over 8 courses, gain in-demand skills that prepare you for an entry-level job. You’ll learn from Google employees whose foundations in data analytics served as launchpads for their own careers. At under 10 hours per week, you can complete the certificate in less than 6 months.'
        )}</text>
        <text>${_t(
          'You’ll prepare yourself for jobs that include junior or associate data analyst, database administrator, and more.'
        )}</text>
        <text>${_t('Check out all Google Career Certificates #{here}', {
          here: '',
        })}<a href="https://www.coursera.org/in/google-career-certificates">here</a>. <strong>${_t(
        'Now INR #{productPrice}/month',
        { productPrice }
      )}</strong>\n${_t('The certificate can be completed in less than 6 months of part time study.')}</text>
      </co-content>
    `,
    };
  },
  [GOOGLE_GATEWAYS_FOR_OVERRIDES_SLUGS.GOOGLE_PROJECT_MANAGEMENT]: (productPrice: number) => {
    return {
      dtdId: '1',
      htmlWithMetadata: {
        html: '',
        metadata: {
          hasAssetBlock: false,
          hasCodeBlock: false,
          hasMath: false,
          isPlainText: true,
        },
      },
      cmlValue: `
      <co-content>
        <text>${_t(
          'Prepare for a new career in the high-growth field of project management, no experience required. Get professional training designed by Google and get on the fast track to a competitively paid job.'
        )}</text>
        <text><strong>${_t('Project managers')}</strong>${_t(
        '#{jobTitle} are natural problem-solvers. They set the plan and guide teammates, and manage changes, risks, and stakeholders.',
        { jobTitle: '' }
      )}</text>
        <text>${_t(
          'Over 6 courses, gain in-demand skills that will prepare you for an entry-level job. Learn from Google employees whose foundations in project management served as launchpads for their own careers. At under 10 hours per week, you can complete in less than six months.'
        )}</text>
        <text>${_t(
          'This program qualifies you for over 100 hours of project management education, which helps prepare you for #{product}',
          { product: '' }
        )}<a href="https://www.pmi.org/">Project Management Institute</a> ${_t(
        'Certifications like the globally-recognized #{product}',
        { product: '' }
      )}<a href="https://www.pmi.org/certifications/certified-associate-capm">Certified Associate in Project Management (CAPM)®</a>.</text>
    <text>${_t('Check out all Google Career Certificates #{here}', {
      here: '',
    })}<a href="https://www.coursera.org/in/google-career-certificates">here</a>. <strong>${_t(
        'Now INR #{productPrice}/month',
        { productPrice }
      )}</strong>\n${_t('The certificate can be completed in less than 6 months of part time study.')}</text>
      </co-content>
    `,
    };
  },
  [GOOGLE_GATEWAYS_FOR_OVERRIDES_SLUGS.GOOGLE_UX_DESIGN]: (productPrice: number) => {
    return {
      dtdId: '1',
      htmlWithMetadata: {
        html: '',
        metadata: {
          hasAssetBlock: false,
          hasCodeBlock: false,
          hasMath: false,
          isPlainText: true,
        },
      },
      cmlValue: `
      <co-content>
        <text>${_t(
          'Prepare for a career in the high-growth field of UX design, no experience required. With professional training designed by Google, get on the fast-track to a competitively paid job.'
        )}</text>
        <text><strong>${_t('User experience (UX) designers')}</strong>${_t(
        '#{jobTitle} focus on the interaction that users have with products, like websites, apps, and physical objects. They make those everyday interactions useful, enjoyable, and accessible.',
        { jobTitle: '' }
      )}</text>
        <text>${_t(
          'Over 7 courses, gain in-demand skills that will prepare you for an entry-level job. At under 10 hours per week, you can complete the certificate in less than 6 months.'
        )}</text>
        <text>${_t(
          'You will create designs on paper and in digital design tools like Figma and Adobe XD. By the end of the certificate program, you will have a professional UX portfolio that includes three end-to-end projects, so that you’re ready to apply for jobs.'
        )}</text>
        <text>${_t('Check out all Google Career Certificates #{here}', {
          here: '',
        })}<a href="https://www.coursera.org/in/google-career-certificates">here</a>. <strong>${_t(
        'Now INR #{productPrice}/month',
        { productPrice }
      )}</strong>\n${_t('The certificate can be completed in less than 6 months of part time study.')}</text>
      </co-content>
    `,
    };
  },
  [GOOGLE_GATEWAYS_FOR_OVERRIDES_SLUGS.GOOGLE_IT_AUTOMATION]: (productPrice: number) => {
    return {
      dtdId: '1',
      htmlWithMetadata: {
        html: '',
        metadata: {
          hasAssetBlock: false,
          hasCodeBlock: false,
          hasMath: false,
          isPlainText: true,
        },
      },
      cmlValue: `
      <co-content>
        <text>${_t(
          'This beginner-level, six-course certificate, developed by Google, is designed to provide IT professionals with in-demand skills -- including Python, Git, and IT automation -- that can help you advance your career.'
        )}</text>
        <text>${_t(
          'Knowing how to write code to solve problems and automate solutions is a crucial skill for anybody in IT.'
        )}</text>
        <text>${_t(
          `This program builds on your IT foundations to help you take your career to the next level. It’s designed to teach you how to program with Python and how to use Python to automate common system administration tasks. You'll also learn to use Git and GitHub, troubleshoot and debug complex problems, and apply automation at scale by using configuration management and the Cloud.`
        )}</text>
        <text>${_t(
          'This certificate can be completed in about 6 months and is designed to prepare you for a variety of roles in IT, like more advanced IT Support Specialist or Junior Systems Administrator positions. '
        )}</text>
        <text>${_t(
          'We recommend that you have Python installed on your machine. For some courses, you’ll need a computer where you can install Git or ask your administrator to install it for you.'
        )}</text>
        <text><strong>${_t(
          'If you’d like to learn the fundamentals of IT support, check out the beginner level #{product} ',
          { product: '' }
        )}<a href="https://www.coursera.org/professional-certificates/google-it-support">Google IT Support Professional Certificate</a>.</strong></text>
        <text>${_t('Check out all Google Career Certificates #{here}', {
          here: '',
        })}<a href="https://www.coursera.org/in/google-career-certificates">here</a>. <strong>${_t(
        'Now INR #{productPrice}/month',
        { productPrice }
      )}</strong>\n${_t('The certificate can be completed in less than 6 months of part time study.')}</text>
      </co-content>
    `,
    };
  },
};

// A plaintext string description is displayed in SSR. CML descriptions are swapped in on CSR.
export const aboutDescriptionOverrides: AboutDescriptionOverrides = {
  [GOOGLE_GATEWAYS_FOR_OVERRIDES_SLUGS.GOOGLE_IT_SUPPORT]: (productPrice: number) => {
    return _t(
      `
      Prepare for a career in the high-growth field of IT, no experience required. Get professional training designed by Google and get on the fast-track to a competitively paid job. Over 5 courses, learn in-demand skills that prepare you for an entry-level job. At under 10 hours per week, you can complete the certificate in less than 6 months. Since the IT Support Certificate launched in 2018, 82% of graduates have reported a positive career outcome like a new job, promotion, or raise within 6 months.1 If you’re interested in building on your IT foundations, check out the Google IT Automation with Python Professional Certificate. Check out all Google Career Certificates here. Now INR #{productPrice}/month The certificate can be completed in less than 6 months of part time study.
    `,
      { productPrice }
    );
  },
  [GOOGLE_GATEWAYS_FOR_OVERRIDES_SLUGS.GOOGLE_DATA_ANALYTICS]: (productPrice: number) => {
    return _t(
      'Get professional training designed by Google and prepare for a new career in the high-growth field of data analytics, no experience required. Data analytics is the collection, transformation, and organization of data in order to draw conclusions, make predictions, and drive informed decision making. Over 8 courses, gain in-demand skills that prepare you for an entry-level job. You’ll learn from Google employees whose foundations in data analytics served as launchpads for their own careers. At under 10 hours per week, you can complete the certificate in less than 6 months. You’ll prepare yourself for jobs that include junior or associate data analyst, database administrator, and more. Check out all Google Career Certificates here. Now INR #{productPrice}/month. The certificate can be completed in less than 6 months of part time study.',
      { productPrice }
    );
  },
  [GOOGLE_GATEWAYS_FOR_OVERRIDES_SLUGS.GOOGLE_PROJECT_MANAGEMENT]: (productPrice: number) => {
    return _t(
      'Prepare for a new career in the high-growth field of project management, no experience required. Get professional training designed by Google and get on the fast track to a competitively paid job. Project managers are natural problem-solvers. They set the plan and guide teammates, and manage changes, risks, and stakeholders. Over 6 courses, gain in-demand skills that will prepare you for an entry-level job. Learn from Google employees whose foundations in project management served as launchpads for their own careers. At under 10 hours per week, you can complete in less than six months. This program qualifies you for over 100 hours of project management education, which helps prepare you for Project Management Institute Certifications like the globally-recognized Certified Associate in Project Management (CAPM)®. Check out all Google Career Certificates here. Now INR #{productPrice}/month. The certificate can be completed in less than 6 months of part time study.',
      {
        productPrice,
      }
    );
  },
  [GOOGLE_GATEWAYS_FOR_OVERRIDES_SLUGS.GOOGLE_UX_DESIGN]: (productPrice: number) => {
    return _t(
      'Prepare for a career in the high-growth field of UX design, no experience required. With professional training designed by Google, get on the fast-track to a competitively paid job. User experience (UX) designers focus on the interaction that users have with products, like websites, apps, and physical objects. They make those everyday interactions useful, enjoyable, and accessible. Over 7 courses, gain in-demand skills that will prepare you for an entry-level job. At under 10 hours per week, you can complete the certificate in less than 6 months. You will create designs on paper and in digital design tools like Figma and Adobe XD. By the end of the certificate program, you will have a professional UX portfolio that includes three end-to-end projects, so that you’re ready to apply for jobs. Check out all Google Career Certificates here. Now INR #{productPrice}/month. The certificate can be completed in less than 6 months of part time study.',
      {
        productPrice,
      }
    );
  },
  [GOOGLE_GATEWAYS_FOR_OVERRIDES_SLUGS.GOOGLE_IT_AUTOMATION]: (productPrice: number) => {
    return _t(
      `This beginner-level, six-course certificate, developed by Google, is designed to provide IT professionals with in-demand skills -- including Python, Git, and IT automation -- that can help you advance your career. Knowing how to write code to solve problems and automate solutions is a crucial skill for anybody in IT. This program builds on your IT foundations to help you take your career to the next level. It’s designed to teach you how to program with Python and how to use Python to automate common system administration tasks. You'll also learn to use Git and GitHub, troubleshoot and debug complex problems, and apply automation at scale by using configuration management and the Cloud. This certificate can be completed in about 6 months and is designed to prepare you for a variety of roles in IT, like more advanced IT Support Specialist or Junior Systems Administrator positions. We recommend that you have Python installed on your machine. For some courses, you’ll need a computer where you can install Git or ask your administrator to install it for you. If you’d like to learn the fundamentals of IT support, check out the beginner level Google IT Support Professional Certificate. Check out all Google Career Certificates here. Now INR #{productPrice}/month. The certificate can be completed in less than 6 months of part time study.
      `,
      {
        productPrice,
      }
    );
  },
};
